/**
 * Voice-first guided tour overlay for review coaches' first run.
 *
 * Mounted from `CoachReviewWelcome` (the /welcome page). Composes:
 *   - BrainOrb (idle / listening / thinking state)
 *   - TourSpotlight (highlights the target UI element through the dim)
 *   - Brain bubble showing the current step's spoken copy
 *   - Large Next + Skip buttons (single-tap targets, labelled clearly)
 *   - Step counter (1 of 5, 2 of 5...)
 *
 * The hook (useFirstRunTour) owns all state + voice + TTS. This
 * component is pure presentation + click wiring.
 */
import { useFirstRunTour } from '../../lib/firstRun/useFirstRunTour';
import { BrainOrb } from '../primitives/BrainOrb';
import { TourSpotlight } from './TourSpotlight';
import { fillSayCopy } from '../../lib/firstRun/firstRunScript';
import { useCoachReviewMode } from '../../hooks/useCoachReviewMode';

export function FirstRunTour(): React.JSX.Element {
  const tour = useFirstRunTour();
  const { coachName } = useCoachReviewMode();
  const { step } = tour;
  const orbState = tour.speaking ? 'thinking' : tour.listening ? 'listening' : 'idle';
  const isFirstStep = tour.stepIndex === 1;
  // Before the coach taps Start, hold on step 1: the tap is the user gesture
  // the browser needs to play Brain's first line (otherwise it's silent).
  const needsStart = isFirstStep && !tour.started;
  const showSpotlight = !!step.target?.selector && !isFirstStep;

  return (
    <>
      {showSpotlight && step.target?.selector && (
        <TourSpotlight selector={step.target.selector} />
      )}
      <div
        role="dialog"
        aria-label="First-run tour"
        data-testid="first-run-tour"
        style={overlayStyle}
      >
        <div style={contentStyle}>
          {/* Step counter chip */}
          <div style={stepCounter}>Step {tour.stepIndex} of {tour.totalSteps}</div>
          {/* Brain orb — visual cue that something is happening */}
          <div style={orbWrap}>
            <BrainOrb size={isFirstStep ? 'xl' : 'lg'} state={orbState} ariaLabel="Brain" />
          </div>
          {/* Brain's spoken copy, also shown as text in case audio fails */}
          <p style={brainSay} data-testid="brain-say">
            {fillSayCopy(step.brainSay, coachName)}
          </p>
          {/* Tap-to-start hint (autoplay gate) on the very first step */}
          {needsStart && (
            <p style={hintStyle}>🔊 Tap Start — Brain will talk you through it.</p>
          )}
          {/* Listening hint on the final step */}
          {step.isFinal && (
            <p style={hintStyle}>
              {tour.listening ? '🎤 Listening — just speak naturally' : 'Tap "Take me there" or speak'}
            </p>
          )}
          {/* Buttons */}
          <div style={btnRow}>
            <button
              type="button"
              onClick={needsStart ? tour.start : tour.next}
              style={primaryBtn}
              disabled={tour.routing}
              data-testid={needsStart ? 'tour-start' : 'tour-next'}
            >
              {needsStart ? '▶ Start' : step.isFinal ? 'Take me to my sessions' : 'Next →'}
            </button>
            <button
              type="button"
              onClick={tour.skip}
              style={secondaryBtn}
              disabled={tour.routing}
              data-testid="tour-skip"
            >
              Skip tour
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---- styles ---------------------------------------------------------------
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 9100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'auto',
};
const contentStyle: React.CSSProperties = {
  maxWidth: 'min(640px, calc(100vw - 48px))',
  padding: '28px 32px 24px',
  borderRadius: 16,
  background: 'rgba(15, 25, 45, 0.92)',
  border: '1px solid rgba(255,255,255,0.18)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
  color: '#fff',
  textAlign: 'center',
  fontFamily: 'system-ui, sans-serif',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
};
const stepCounter: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  opacity: 0.55,
  padding: '4px 12px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 999,
};
const orbWrap: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '4px 0',
};
const brainSay: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  lineHeight: 1.5,
  fontWeight: 500,
  letterSpacing: 0.1,
};
const hintStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  opacity: 0.7,
  fontStyle: 'italic',
};
const btnRow: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  marginTop: 8,
  flexWrap: 'wrap',
  justifyContent: 'center',
};
const primaryBtn: React.CSSProperties = {
  padding: '12px 24px',
  background: '#27AE60',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  minWidth: 140,
};
const secondaryBtn: React.CSSProperties = {
  padding: '10px 18px',
  background: 'transparent',
  color: 'rgba(255,255,255,0.65)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 999,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
